/**
 * Tooltip — infobulle contextuelle au survol / focus.
 *
 * Le contrat markup est INCHANGÉ (rétrocompat) :
 *   <span class="has-tooltip" data-tooltip="…" data-placement="top|bottom|left|right"
 *         data-variant="info|success|warning|danger">…</span>
 *
 * Auparavant l'infobulle était un pseudo-élément ::after en position:absolute,
 * rogné par tout ancêtre à overflow (ex. .table-wrapper). initTooltips prend
 * désormais le relais : il crée un vrai élément .ds-tooltip inséré à la racine
 * du <body> (hors de tout conteneur de scroll) et le positionne en fixed —
 * plus aucun rognage, y compris dans les tableaux.
 *
 * RÉTROCOMPAT / FALLBACK : le ::after CSS n'est masqué que via la classe
 * .has-tooltip--js, posée ici uniquement quand le JS a effectivement pris le
 * relais sur un élément. Si ce script ne tourne pas, le ::after d'origine reste
 * affiché : le comportement actuel est préservé, aucune régression.
 */
export function initTooltips(container = document) {
  const els = container.querySelectorAll('.has-tooltip');
  let uid = 0;

  els.forEach((el) => {
    if (el.dataset.tooltipBound) return; // évite le double-binding
    el.dataset.tooltipBound = 'true';

    let tip = null;

    function show() {
      if (tip) return; // déjà affiché (survol + focus cumulés) : idempotent
      const text = el.getAttribute('data-tooltip');
      if (!text) return;

      // Le JS prend le relais : on masque le ::after d'origine sur cet élément.
      el.classList.add('has-tooltip--js');

      tip = document.createElement('div');
      tip.className = 'ds-tooltip';
      tip.setAttribute('role', 'tooltip');
      tip.id = `ds-tooltip-${++uid}`;
      tip.textContent = text;

      // Reporter les modificateurs (variante, alignement, largeur) sur le div.
      const variant = el.getAttribute('data-variant');
      if (variant) tip.classList.add(`ds-tooltip--${variant}`);
      ['text-left', 'text-center', 'text-right', 'sm', 'md', 'lg'].forEach((mod) => {
        if (el.classList.contains(`tooltip-${mod}`)) tip.classList.add(`ds-tooltip--${mod}`);
      });

      // Dans la modale ouverte : hors d'elle, showModal() rend le contenu inerte (ignoré des lecteurs d'écran).
      (el.closest('dialog[open]') || document.body).appendChild(tip);
      // Top layer : sinon l'infobulle passe sous un <dialog> showModal() ou un popover ouvert.
      if (typeof tip.showPopover === 'function') {
        tip.setAttribute('popover', 'manual');
        tip.showPopover();
      }
      el.setAttribute('aria-describedby', tip.id);

      position();
      // Rendre visible au frame suivant pour jouer la transition.
      requestAnimationFrame(() => tip && tip.classList.add('ds-tooltip--visible'));
    }

    function position() {
      if (!tip) return;
      const a = el.getBoundingClientRect();
      const t = tip.getBoundingClientRect();
      const gap = 6;
      const vw = document.documentElement.clientWidth;
      const vh = document.documentElement.clientHeight;
      let placement = el.getAttribute('data-placement') || 'top';

      // coordonnées candidates pour un placement donné
      const coord = (p) => {
        switch (p) {
          case 'bottom': return { x: a.left + a.width / 2 - t.width / 2, y: a.bottom + gap };
          case 'left':   return { x: a.left - t.width - gap,             y: a.top + a.height / 2 - t.height / 2 };
          case 'right':  return { x: a.right + gap,                      y: a.top + a.height / 2 - t.height / 2 };
          default:       return { x: a.left + a.width / 2 - t.width / 2, y: a.top - t.height - gap }; // top
        }
      };
      // flip si le placement demandé déborde du viewport
      const opposite = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
      let pos = coord(placement);
      const overflows = (p) =>
        (p === 'top' && pos.y < 0) ||
        (p === 'bottom' && pos.y + t.height > vh) ||
        (p === 'left' && pos.x < 0) ||
        (p === 'right' && pos.x + t.width > vw);
      if (overflows(placement)) {
        const flipped = coord(opposite[placement]);
        pos = flipped;
        placement = opposite[placement];
      }
      // clamp horizontal/vertical dans le viewport (marge de 4px)
      pos.x = Math.max(4, Math.min(pos.x, vw - t.width - 4));
      pos.y = Math.max(4, Math.min(pos.y, vh - t.height - 4));

      tip.style.left = `${Math.round(pos.x)}px`;
      tip.style.top = `${Math.round(pos.y)}px`;
    }

    function hide() {
      el.removeAttribute('aria-describedby');
      el.classList.remove('has-tooltip--js');
      if (tip) {
        if (tip.matches(':popover-open')) tip.hidePopover();
        tip.remove();
        tip = null;
      }
    }

    // On suit survol ET focus séparément : le tooltip est visible tant que l'un
    // des deux est actif, et masqué seulement quand les deux sont retombés.
    // Sinon, un clic (qui pose le focus) laisse le tooltip collé après le mouseleave.
    let hovered = false;
    let focused = false;
    const sync = () => { (hovered || focused) ? show() : hide(); };

    el.addEventListener('mouseenter', () => { hovered = true; sync(); });
    el.addEventListener('mouseleave', () => { hovered = false; sync(); });
    // Focus clavier uniquement : sinon le focus auto de showModal() ou un clic souris affichent l'infobulle sans survol.
    el.addEventListener('focus', () => { focused = el.matches(':focus-visible'); sync(); });
    el.addEventListener('blur', () => { focused = false; sync(); });
    // Échap ferme même si l'élément garde le focus (ex. après un clic).
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { hovered = false; focused = false; hide(); }
    });
    // Le fixed suit le viewport, pas l'ancre : on ferme au scroll.
    window.addEventListener('scroll', () => {
      if (tip) { hovered = false; focused = false; hide(); }
    }, true);
  });

  // Compat : même API de mise à jour du texte qu'avant.
  function updateTooltipText(el, text) {
    el.setAttribute('data-tooltip', text);
  }
  return { updateTooltipText };
}
